import React from 'react';
import { ContentPositioner } from '../SideBySide/ContentPositioner';
import Image from '@jetshop/ui/Image/Image';
import { Above } from '@jetshop/ui/Breakpoints';
import { Link } from 'react-router-dom';
import { cx } from 'linaria';
import { styled } from 'linaria/react';
import { theme } from '../../../Theme';
import { FakeCTA } from './FakeCTA';

const HtmlBannerRowItemWrapper = styled(Link)`
  position: relative;
  text-decoration: none;
  display: block;

  --thirtyPercentBlack: #0000004d;
  --fiftyPercentBlack: #00000080;
  --gradientColor: ${props => props.gradientcolor};

  .content-positioner {
    position: absolute;
    padding: 40px 30px;
  }

  &.only-child {
    .content-positioner {
      padding: 80px;

      ${theme.below.lg} {
        padding: 40px 30px;
      }
    }
  }

  .desktop-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    &.hide {
      display: none;
    }

    &.top-left {
      background: radial-gradient(
        ellipse at left top,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.middle-left {
      background: radial-gradient(
        ellipse at left center,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.bottom-left {
      background: radial-gradient(
        ellipse at left bottom,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }
    &.top-center {
      background: radial-gradient(
        ellipse at center top,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.middle-center {
      background: radial-gradient(
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.bottom-center {
      background: radial-gradient(
        ellipse at center bottom,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.top-right {
      background: radial-gradient(
        ellipse at right top,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.middle-right {
      background: radial-gradient(
        ellipse at right center,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    &.bottom-right {
      background: radial-gradient(
        ellipse at right bottom,
        var(--gradientColor),
        var(--gradientColor),
        transparent,
        transparent 95%
      );
    }

    ${theme.below.lg} {
      &.top-left,
      &.top-center,
      &.top-right {
        background: linear-gradient(
          to bottom,
          var(--gradientColor),
          var(--gradientColor),
          transparent,
          transparent 95%
        );
      }

      &.middle-left,
      &.middle-center,
      &.middle-right {
        background: linear-gradient(
          to bottom,
          transparent,
          var(--gradientColor),
          var(--gradientColor),
          transparent
        );
      }

      &.bottom-left,
      &.bottom-center,
      &.bottom-right {
        background: linear-gradient(
          to top,
          var(--gradientColor),
          var(--gradientColor),
          transparent,
          transparent 95%
        );
      }
    }

    &.not-only-child {
      &.top-left,
      &.top-center,
      &.top-right {
        background: linear-gradient(
          to bottom,
          var(--gradientColor),
          var(--gradientColor),
          transparent,
          transparent 95%
        );
      }

      &.middle-left,
      &.middle-center,
      &.middle-right {
        background: linear-gradient(
          to bottom,
          transparent,
          var(--gradientColor),
          var(--gradientColor),
          transparent
        );
      }

      &.bottom-left,
      &.bottom-center,
      &.bottom-right {
        background: linear-gradient(
          to top,
          var(--gradientColor),
          var(--gradientColor),
          transparent,
          transparent 95%
        );
      }
    }
  }

  .html-banner-row-item-content {
    max-width: 600px;
    h1,
    h2,
    h3 {
      font-family: ${theme.fonts.primary};
      font-size: 28px;
      font-style: normal;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 12px;
    }
    p {
      line-height: normal;
    }

    &.light {
      color: ${theme.colors.white};
    }
    &.dark {
      color: ${theme.colors.black};
    }

    /* &.only-child {
      h1,
      h2,
      h3 {
        font-size: 60px;
      }
    } */
  }

  .fake-cta {
    margin-top: 20px;

    &.left {
      margin-left: 0;
      margin-right: auto;
    }
    &.center {
      margin-left: auto;
      margin-right: auto;
    }
    &.right {
      margin-right: 0;
      margin-left: auto;
    }

    &.button-separate {
      position: absolute;
      top: auto;
      bottom: 2rem;
      left: 50%;
      right: auto;
      transform: translateX(-50%);
    }
  }
`;

const ContentUnderImageWrapper = styled('div')`
  padding: 40px 0px;

  ${theme.below.lg} {
    padding: 40px 30px;
  }

  .html-banner-row-item-content {
    text-align: left;
    max-width: 100%;

    &.left {
      text-align: left;
    }
    &.center {
      text-align: center;
    }
    &.right {
      text-align: right;
    }
  }
`;

export const HtmlBannerRowItem = ({
  desktopImage,
  desktopImageAlt,
  desktopImageAspect,
  mobileImage,
  mobileImageAlt,
  mobileImageAspect,
  contentTheme,
  desktopContent,
  mobileContent,
  desktopContentPosition,
  mobileContentPosition,
  contentBelowImage,
  contentBackdrop,
  buttonSeparate,
  buttonText,
  ghostButton,
  link,
  onlyChild
}) => {
  const wrapperProps = {};

  if (link?.value) {
    wrapperProps.to = link?.value;
    wrapperProps.ariaLabel = buttonText?.value || 'Link';
  } else {
    wrapperProps.as = 'div';
  }

  let buttonTheme = 'black';
  switch (contentTheme?.value) {
    case 'light':
      buttonTheme = 'light';
      break;
    case 'dark':
      buttonTheme = 'primary';
      break;
    default:
      console.info(
        'No content theme found for HTML Banner Row Item, defaulting to black'
      );
      break;
  }

  if (ghostButton?.value) {
    buttonTheme += ' ghost';
  }
  const desktopVerticalPosition =
    desktopContentPosition?.value?.split('-')[0] ?? 'middle';
  const mobileVerticalPosition =
    mobileContentPosition?.value?.split('-')[0] ?? 'middle';
  const desktopHorizontalPosition =
    desktopContentPosition?.value?.split('-')[1] ?? 'center';
  const mobileHorizontalPosition =
    mobileContentPosition?.value?.split('-')[1] ?? 'center';

  const thirtyPercentBlack = '#0000004d';
  const fiftyPercentBlack = '#00000080';
  const transparent = 'transparent';

  let gradientColor = transparent;

  switch (contentBackdrop?.value) {
    case 'none':
      gradientColor = transparent;
      break;
    case '30%':
      gradientColor = thirtyPercentBlack;
      break;
    case '50%':
      gradientColor = fiftyPercentBlack;
      break;
  }
  wrapperProps.gradientcolor = gradientColor;

  const sizes = [1, 1, 1 / 2, 1 / 2, 1 / 2];
  const sizesOnlyChild = [1, 1, 1, 1, 1];

  return (
    <HtmlBannerRowItemWrapper
      className={cx('html-banner-row-item', onlyChild?.value && 'only-child')}
      {...wrapperProps}
    >
      <Above breakpoint="lg">
        {matches => (
          <>
            <Image
              aspect={
                matches ? desktopImageAspect?.value : mobileImageAspect?.value
              }
              sizes={onlyChild?.value ? sizesOnlyChild : sizes}
              cover={true}
              src={matches ? desktopImage?.value : mobileImage?.value}
              alt={matches ? desktopImageAlt?.value : mobileImageAlt?.value}
            />
            {!contentBelowImage?.value && (
              <>
                <div
                  gradientcolor={gradientColor}
                  className={cx(
                    'desktop-backdrop',
                    matches
                      ? desktopContentPosition.value ?? 'middle-center'
                      : mobileContentPosition.value ?? 'middle-center',
                    onlyChild?.value ? 'only-child' : 'not-only-child'
                  )}
                />
                <ContentPositioner
                  verticalPosition={(matches
                    ? desktopVerticalPosition
                    : mobileVerticalPosition
                  )?.toUpperCase()}
                  horizontalPosition={(matches
                    ? desktopHorizontalPosition
                    : mobileHorizontalPosition
                  )?.toUpperCase()}
                >
                  <div>
                    <div
                      className={cx(
                        'html-banner-row-item-content',
                        contentTheme?.value ?? 'dark',
                        onlyChild?.value && 'only-child'
                      )}
                      dangerouslySetInnerHTML={{
                        __html: matches
                          ? desktopContent?.value
                          : mobileContent?.value
                      }}
                    />
                    {buttonText?.value?.length > 0 && (
                      <FakeCTA
                        className={cx(
                          buttonTheme,
                          buttonSeparate?.value && 'button-separate'
                        )}
                      >
                        {buttonText?.value}
                      </FakeCTA>
                    )}
                  </div>
                </ContentPositioner>
              </>
            )}
            {contentBelowImage?.value && (
              <ContentUnderImageWrapper>
                <div
                  className={cx(
                    'html-banner-row-item-content',
                    'dark',
                    matches
                      ? desktopHorizontalPosition
                      : mobileHorizontalPosition
                  )}
                  dangerouslySetInnerHTML={{
                    __html: matches
                      ? desktopContent?.value
                      : mobileContent?.value
                  }}
                />
                {buttonText?.value?.length > 0 && (
                  <FakeCTA
                    className={cx(
                      'primary',
                      matches
                        ? desktopHorizontalPosition
                        : mobileHorizontalPosition
                    )}
                  >
                    {buttonText?.value}
                  </FakeCTA>
                )}
              </ContentUnderImageWrapper>
            )}
          </>
        )}
      </Above>
    </HtmlBannerRowItemWrapper>
  );
};
